import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown, Edit2, Check, X, Plus, Trash2, RotateCcw, Save, Folder } from 'lucide-react';
import api from '../api/client';
import { VOCE, ICONE, completaLayout, useMenuStaff, ricaricaMenuStaff } from './menuStaff';

// Impostazioni → Menu laterale (solo ROOT, 23/10/2026): ordine, menu e nomi della sidebar staff.
// Qui si sposta e si rinomina soltanto: chi vede una voce lo decidono sempre i permessi del ruolo.
const copia = (l) => JSON.parse(JSON.stringify(l));
const MAX_NOME = 40;

export default function EditorMenuLaterale() {
  const salvato = useMenuStaff();
  const [layout, setLayout] = useState(() => copia(completaLayout(salvato)));
  const [modificato, setModificato] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [rinomina, setRinomina] = useState(null); // { chiave, valore } chiave = path o 'g:<id>'
  const [iconeDi, setIconeDi] = useState(null);   // id del menu di cui scegliere l'icona

  // Quando arriva il layout dal server (o dopo un salvataggio) si riparte da lì
  useEffect(() => { setLayout(copia(completaLayout(salvato))); setModificato(false); }, [salvato]);

  const cambia = (fn) => { setLayout(l => { const n = copia(l); fn(n); return n; }); setModificato(true); };
  const nomeVoce = (p) => layout.nomi[p] || VOCE[p]?.label || p;
  const gruppi = layout.voci.filter(v => v.tipo === 'gruppo');

  // lista = array dove sta l'elemento (primo livello o figli di un menu)
  const sposta = (lista, i, d) => { const j = i + d; if (j < 0 || j >= lista.length) return; [lista[i], lista[j]] = [lista[j], lista[i]]; };
  const listaDi = (n, gid) => gid ? n.voci.find(v => v.id === gid).figli : n.voci;

  const muovi = (gid, i, d) => cambia(n => sposta(listaDi(n, gid), i, d));

  // Sposta una voce in un altro menu (in fondo) o al primo livello ('')
  const spostaIn = (path, daGid, aGid) => cambia(n => {
    if (daGid) { const g = n.voci.find(v => v.id === daGid); g.figli = g.figli.filter(p => p !== path); }
    else n.voci = n.voci.filter(v => !(v.tipo === 'voce' && v.path === path));
    if (aGid) n.voci.find(v => v.id === aGid).figli.push(path);
    else n.voci.push({ tipo: 'voce', path });
  });

  const nuovoMenu = () => {
    const label = (window.prompt('Nome del nuovo menu:') || '').trim();
    if (!label) return;
    if (label.length > MAX_NOME) { alert(`Al massimo ${MAX_NOME} caratteri`); return; }
    const id = 'menu-' + Date.now().toString(36);
    cambia(n => { n.voci.push({ tipo: 'gruppo', id, label, icon: 'Folder', figli: [] }); });
  };

  const eliminaMenu = (g) => {
    if (g.figli.length) { alert('Prima sposta altrove le voci di questo menu.'); return; }
    if (!window.confirm(`Eliminare il menu "${g.label}"?`)) return;
    cambia(n => { n.voci = n.voci.filter(v => v.id !== g.id); });
  };

  const confermaRinomina = () => {
    const { chiave, valore } = rinomina;
    const v = valore.trim();
    if (v.length > MAX_NOME) { alert(`Al massimo ${MAX_NOME} caratteri`); return; }
    if (chiave.startsWith('g:')) {
      if (!v) { alert('Il menu deve avere un nome'); return; }
      cambia(n => { n.voci.find(x => x.id === chiave.slice(2)).label = v; });
    } else {
      // Vuoto o uguale all'originale = torna al nome scritto nel codice
      cambia(n => { if (!v || v === VOCE[chiave].label) delete n.nomi[chiave]; else n.nomi[chiave] = v; });
    }
    setRinomina(null);
  };

  const salva = async () => {
    setSalvando(true);
    try { await api.put('/menu/STAFF', layout); await ricaricaMenuStaff(); }
    catch (e) { alert(e.response?.data?.error || 'Salvataggio non riuscito'); }
    finally { setSalvando(false); }
  };

  const ripristina = async () => {
    if (!window.confirm('Tornare al menu originale? Si perdono ordine, menu creati e nomi cambiati.')) return;
    setSalvando(true);
    try { await api.delete('/menu/STAFF'); await ricaricaMenuStaff(); }
    catch (e) { alert(e.response?.data?.error || 'Ripristino non riuscito'); }
    finally { setSalvando(false); }
  };

  const campoNome = (chiave) => (
    <span style={{ display:'flex', gap:'4px', alignItems:'center', flex:'1 1 220px', minWidth:0 }}>
      <input autoFocus value={rinomina.valore} maxLength={MAX_NOME}
        onChange={e => setRinomina(r => ({ ...r, valore: e.target.value }))}
        onKeyDown={e => { if (e.key === 'Enter') confermaRinomina(); if (e.key === 'Escape') setRinomina(null); }}
        placeholder={chiave.startsWith('g:') ? '' : VOCE[chiave]?.label}
        style={{ ...st.input, flex:1, minWidth:0 }}/>
      <button style={st.ico} title="Conferma" onClick={confermaRinomina}><Check size={14} color="#22C55E"/></button>
      <button style={st.ico} title="Annulla" onClick={() => setRinomina(null)}><X size={14}/></button>
    </span>
  );

  const frecce = (gid, i, tot) => (
    <>
      <button style={st.ico} title="Su" disabled={i === 0} onClick={() => muovi(gid, i, -1)}><ArrowUp size={14} style={{ opacity: i === 0 ? .3 : 1 }}/></button>
      <button style={st.ico} title="Giù" disabled={i === tot - 1} onClick={() => muovi(gid, i, 1)}><ArrowDown size={14} style={{ opacity: i === tot - 1 ? .3 : 1 }}/></button>
    </>
  );

  const rigaVoce = (path, gid, i, tot) => {
    const V = VOCE[path];
    const Icona = V.icon;
    const rinominata = !!layout.nomi[path];
    return (
      <div key={path} style={{ ...st.riga, ...(gid ? { marginLeft:'22px' } : {}) }}>
        <Icona size={14} color="var(--text-dim)" style={{ flexShrink:0 }}/>
        {rinomina?.chiave === path ? campoNome(path) : (
          <span style={{ flex:'1 1 150px', minWidth:0 }}>
            <span style={st.nome}>{nomeVoce(path)}</span>
            {rinominata && <span style={st.orig}> (era: {V.label})</span>}
          </span>
        )}
        {rinomina?.chiave !== path && <button style={st.ico} title="Rinomina" onClick={() => setRinomina({ chiave: path, valore: nomeVoce(path) })}><Edit2 size={13}/></button>}
        {frecce(gid, i, tot)}
        <select value={gid || ''} onChange={e => spostaIn(path, gid, e.target.value)} style={st.select} title="Sposta in…">
          <option value="">Primo livello</option>
          {gruppi.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
        </select>
      </div>
    );
  };

  return (
    <div>
      <div style={st.spiega}>
        Qui decidi <b>ordine</b>, <b>menu</b> e <b>nomi</b> della barra laterale dello staff. Vale per tutti.
        Spostare o rinominare una voce <b>non cambia chi la vede</b>: lo decidono sempre i permessi del ruolo,
        e un menu sparisce da solo per chi non ha nessuna delle sue voci. Le pagine nuove compaiono da sole nel loro posto di sempre.
      </div>

      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', margin:'14px 0' }}>
        <button style={{ ...st.btnPrim, opacity: !modificato || salvando ? .45 : 1 }} disabled={!modificato || salvando} onClick={salva}>
          <Save size={13}/> {salvando ? 'SALVO…' : 'SALVA MENU'}
        </button>
        <button style={{ ...st.btn, opacity: !modificato || salvando ? .45 : 1 }} disabled={!modificato || salvando} onClick={() => { setLayout(copia(completaLayout(salvato))); setModificato(false); setRinomina(null); }}>
          <X size={13}/> ANNULLA MODIFICHE
        </button>
        <button style={st.btn} onClick={nuovoMenu}><Plus size={13}/> NUOVO MENU</button>
        <button style={{ ...st.btn, marginLeft:'auto' }} disabled={salvando || !salvato} onClick={ripristina} title="Torna al menu scritto nel codice">
          <RotateCcw size={13}/> RIPRISTINA MENU ORIGINALE
        </button>
      </div>
      {modificato && <div style={{ fontSize:'var(--font-size-xs)', color:'#F59E0B', marginBottom:'10px', fontWeight:700 }}>Modifiche non ancora salvate</div>}

      <div style={{ display:'flex', flexDirection:'column', gap:'4px', maxWidth:'720px' }}>
        {layout.voci.map((v, i) => v.tipo === 'voce'
          ? rigaVoce(v.path, null, i, layout.voci.length)
          : (() => {
            const Icona = ICONE[v.icon] || Folder;
            const chiave = 'g:' + v.id;
            return (
              <div key={v.id} style={st.gruppo}>
                <div style={{ ...st.riga, background:'transparent', border:'none', padding:'4px 6px' }}>
                  <button style={st.ico} title="Cambia icona" onClick={() => setIconeDi(iconeDi === v.id ? null : v.id)}><Icona size={15} color="#F4600C"/></button>
                  {rinomina?.chiave === chiave ? campoNome(chiave) : (
                    <span style={{ ...st.nome, fontWeight:800, flex:'1 1 150px', minWidth:0 }}>{v.label} <span style={st.orig}>· {v.figli.length} voci</span></span>
                  )}
                  {rinomina?.chiave !== chiave && <button style={st.ico} title="Rinomina menu" onClick={() => setRinomina({ chiave, valore: v.label })}><Edit2 size={13}/></button>}
                  {frecce(null, i, layout.voci.length)}
                  <button style={st.ico} title="Elimina menu" onClick={() => eliminaMenu(v)}><Trash2 size={13} color={v.figli.length ? 'var(--text-dim)' : '#EF4444'}/></button>
                </div>
                {iconeDi === v.id && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', padding:'4px 8px 8px' }}>
                    {Object.entries(ICONE).map(([nome, I]) => (
                      <button key={nome} title={nome} onClick={() => { cambia(n => { n.voci.find(x => x.id === v.id).icon = nome; }); setIconeDi(null); }}
                        style={{ ...st.ico, border: nome === v.icon ? '1px solid #F4600C' : '1px solid var(--border)', borderRadius:'6px', padding:'6px' }}>
                        <I size={15}/>
                      </button>
                    ))}
                  </div>
                )}
                {v.figli.length === 0 && <div style={{ ...st.orig, padding:'2px 0 6px 28px' }}>Menu vuoto: spostaci delle voci con "Sposta in…". Vuoto non si vede nella barra.</div>}
                {v.figli.map((p, j) => rigaVoce(p, v.id, j, v.figli.length))}
              </div>
            );
          })())}
      </div>
    </div>
  );
}

const st = {
  spiega:  { fontSize:'var(--font-size-sm)', color:'var(--text-muted)', lineHeight:1.5, maxWidth:'720px' },
  riga:    { display:'flex', alignItems:'center', gap:'6px', padding:'6px 8px', borderRadius:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', flexWrap:'wrap' },
  gruppo:  { border:'1px solid var(--border)', borderRadius:'10px', padding:'4px', display:'flex', flexDirection:'column', gap:'4px', background:'var(--bg-input)' },
  nome:    { fontSize:'var(--font-size-sm)', fontWeight:600, color:'var(--text)' },
  orig:    { fontSize:'var(--font-size-xs)', color:'var(--text-dim)', fontWeight:400 },
  ico:     { background:'transparent', border:'none', cursor:'pointer', padding:'4px', display:'flex', color:'var(--text-muted)' },
  select:  { fontSize:'var(--font-size-xs)', padding:'4px 6px', borderRadius:'6px', border:'1px solid var(--border)', background:'var(--bg-input)', color:'var(--text)', maxWidth:'160px' },
  input:   { fontSize:'var(--font-size-sm)', padding:'4px 8px', borderRadius:'6px', border:'1px solid var(--border)', background:'var(--bg-input)', color:'var(--text)' },
  btn:     { display:'flex', alignItems:'center', gap:'6px', padding:'8px 12px', borderRadius:'8px', border:'1px solid var(--border)', background:'transparent', color:'var(--text)', fontSize:'var(--font-size-xs)', fontWeight:700, letterSpacing:'.5px', cursor:'pointer' },
  btnPrim: { display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'8px', border:'none', background:'#F4600C', color:'#fff', fontSize:'var(--font-size-xs)', fontWeight:700, letterSpacing:'.5px', cursor:'pointer' },
};
